/*
    不良画像編集
*/
import {
  ChangeEventHandler,
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  ComponentPropsWithoutRef,
  useImperativeHandle,
} from "react";
import { Slider } from "@mui/material";
import { fabric } from "fabric";
import "./DefectsCanvas.css";
// ビデオキャプチャ
import Webcam from "react-webcam";

// 動作モード
export type DefectsInputMode = "capture" | "rect" | "freehand" | "text";

type DefectsCanvasProps = ComponentPropsWithoutRef<"div"> & {
  // document idを一意に割り振るため親から値を受け取る
  canvasId: string;
};

export type DefectsCanvasRefMethods = {
  getDefectImage: () => string;
};

let isRedoing: boolean = false;
let canvasHistory: fabric.Object[] = [];
const initCanvasWidth = 1024;
const initCanvasHeight = 576;

export const DefectsCanvas = forwardRef<
  DefectsCanvasRefMethods,
  DefectsCanvasProps
>(function Child({ ...props }, ref) {
  // 初期設定
  const initialBrushWidth = 3;
  const initialBrushColor = "#000000";
  const brushMinWidth = 1;
  const brushMaxWidth = 30;
  const [inputMode, setInputMode] = useState<DefectsInputMode>("freehand");
  const webcamRef = useRef<Webcam>(null);
  const fabricCanvasRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectColor, setSelectColor] = useState(initialBrushColor);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [brushwidth, setBrushWidth] = useState<number>(initialBrushWidth);
  const canvasId = props.canvasId;
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(initCanvasWidth);
  const [canvasHeight, setCanvasHeight] = useState(initCanvasHeight);
  const videoConstraints = {
    width: canvasWidth,
    height: canvasHeight,
    facingMode: "user",
  };
  const [isScroll, setIsScroll] = useState<boolean>(true);
  // 画像が削除されているか判定用
  let deleteFlag = false;

  // 画像をテキストに変換して返す
  const getDefectImage = () => {
    if (canvasRef !== undefined && deleteFlag === false) {
      const data = canvasRef.current?.toDataURL("image/jpeg", 0.75);
      if (data !== undefined) {
        return data;
      }
    }
    return "";
  };

  // 親コンポーネントに画像取得メソッドの参照を設定
  useImperativeHandle(
    ref,
    () => {
      return {
        getDefectImage,
      };
    },
    []
  );

  // ブラシサイズ変更
  const handleChangeWidth = (_event: Event, value: number | number[]) => {
    if (typeof value === "number") setBrushWidth(value);
  };
  // ブラシサイズ変更をFabric.jsのプロパティに反映
  useEffect(() => {
    if (fabricCanvasRef != null && fabricCanvasRef.current != null) {
      fabricCanvasRef.current.freeDrawingBrush.width = brushwidth;
    }
  }, [brushwidth]);

  // 写真を選択
  const handleSelectFile: ChangeEventHandler<HTMLInputElement> = async (
    event
  ) => {
    setIsScroll(true);
    if (
      event.target.files?.length !== undefined &&
      event.target.files?.length > 0 &&
      event.target.files !== null
    ) {
      const imageSrc = window.URL.createObjectURL(event.target.files[0]);
      fabricCanvasRef.current.setBackgroundImage(imageSrc, (e: any) => {
        setCanvasWidth(e.width);
        setCanvasHeight(e.height);
        fabricCanvasRef.current.setDimensions({
          width: e.width,
          height: e.height,
        });
        fabricCanvasRef.current.renderAll();
      });
      const timerId = setInterval(() => {
        fabricCanvasRef.current.renderAll();
        clearInterval(timerId);
      }, 1000);
      setInputMode("freehand");
    }
  };

  // 画面キャプチャをcanvasへセット
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      fabric.Image.fromURL(imageSrc, (oImg) => {
        fabricCanvasRef.current.setBackgroundImage(oImg, () =>
          fabricCanvasRef.current.renderAll()
        );
      });
    }
    setInputMode("freehand");
  }, [webcamRef]);

  // 撮るモードへ
  const setCaptureMode = () => {
    setIsScroll(false);
    setCanvasWidth(initCanvasWidth);
    setCanvasHeight(initCanvasHeight);
    setInputMode("capture");
    clearCanvasEvent();
  };

  // 手書きモードへ
  const setFreehandMode = () => {
    setIsScroll(true);
    fabricCanvasRef.current.isDrawingMode = true;
    fabricCanvasRef.current.selection = false;
    setInputMode("freehand");
    clearCanvasEvent();
  };

  // 四角を描くモードへ
  const setRectMode = () => {
    setIsScroll(true);
    clearCanvasEvent();
    fabricCanvasRef.current.isDrawingMode = false;
    fabricCanvasRef.current.selection = false;
    fabricCanvasRef.current.on("mouse:down", (e: any) => mousedown(e));
    fabricCanvasRef.current.on("mouse:move", (e: any) => mousemove(e));
    fabricCanvasRef.current.on("mouse:up", (e: any) => mouseup(e));
    setInputMode("rect");
  };

  // テキストモードへ
  const setTextMode = () => {
    setIsScroll(true);
    clearCanvasEvent();
    fabricCanvasRef.current.on("mouse:down", (e: any) => mousedownText(e));
    fabricCanvasRef.current.isDrawingMode = false;
    fabricCanvasRef.current.selection = false;
    setInputMode("text");
  };

  // 元に戻す
  const undo = () => {
    const canvas = fabricCanvasRef.current;
    if (canvas !== undefined && canvas._objects.length > 0) {
      const copyArray = [...canvasHistory];
      copyArray.push(canvas._objects.pop()!);
      canvasHistory = copyArray;
      canvas.renderAll();
    }
  };

  // redo時の処理
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    canvas?.on("object:added", () => {
      if (!isRedoing) {
        canvasHistory = [];
      }
      isRedoing = false;
    });
  }, []);

  // やり直し
  const redo = () => {
    const canvas = fabricCanvasRef.current;
    if (canvas !== undefined && canvasHistory.length > 0) {
      isRedoing = true;
      canvas.add(canvasHistory.pop()!);
    }
  };

  // Fabric.jsの初期化
  useEffect(() => {
    fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
      isDrawingMode: true, // 手書きモード
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: "#f9f8f4",
    });
    // ブラシ初期サイズ
    fabricCanvasRef.current.freeDrawingBrush.width = 3;
    // ブラシ初期色
    if (fabricCanvasRef.current !== undefined) {
      fabricCanvasRef.current.freeDrawingBrush.color = initialBrushColor;
    }
    //setFabricCanvas(fabricCanvasRef.current);
    return () => fabricCanvasRef.current.dispose();
  }, []);

  const clearCanvasEvent = () => {
    setIsShowTextArea(false);
    // ひとつも存在しないとエラーになるので・・
    fabricCanvasRef.current.on("mouse:down", (e: any) => {});
    fabricCanvasRef.current.on("mouse:move", (e: any) => {});
    fabricCanvasRef.current.on("mouse:up", (e: any) => {});
    fabricCanvasRef.current.__eventListeners["mouse:down"] = [];
    fabricCanvasRef.current.__eventListeners["mouse:move"] = [];
    fabricCanvasRef.current.__eventListeners["mouse:up"] = [];
  };

  let rectMode = false;
  let origX: number;
  let origY: number;

  // Rect描画イベント
  const mousedown = (o: any) => {
    rectMode = true;

    let pointer = fabricCanvasRef.current.getPointer(o.e);
    origX = pointer.x;
    origY = pointer.y;

    let rect = new fabric.Rect({
      left: origX,
      top: origY,
      originX: "left",
      originY: "top",
      width: pointer.x - origX,
      height: pointer.y - origY,
      angle: 0,
      transparentCorners: false,
      hasBorders: false,
      hasControls: false,
      strokeWidth: brushwidth,
    });

    fabricCanvasRef.current.add(rect).setActiveObject(rect);
  };
  // Rect描画イベント
  const mousemove = (o: any) => {
    if (!rectMode) return;
    let pointer = fabricCanvasRef.current.getPointer(o.e);
    let activeObj = fabricCanvasRef.current.getActiveObject();
    activeObj.stroke = fabricCanvasRef.current.freeDrawingBrush.color;
    activeObj.strokeWidth = fabricCanvasRef.current.freeDrawingBrush.width;
    //activeObj.strokeWidth= brushwidth;
    activeObj.fill = "transparent";
    if (origX > pointer.x) {
      activeObj.set({ left: Math.abs(pointer.x) });
    }
    if (origY > pointer.y) {
      activeObj.set({ top: Math.abs(pointer.y) });
    }
    activeObj.set({ width: Math.abs(origX - pointer.x) });
    activeObj.set({ height: Math.abs(origY - pointer.y) });
    activeObj.setCoords();
    fabricCanvasRef.current.renderAll();
  };
  // Rect描画イベント
  const mouseup = (o: any) => {
    rectMode = false;
  };

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isShowTextArea, setIsShowTextArea] = useState<boolean>(false);
  const [textAreaPosX, setTextAreaPosX] = useState<number>(0);
  const [textAreaPosY, setTextAreaPosY] = useState<number>(0);

  // Text入力イベント
  const mousedownText = (o: any) => {
    setIsShowTextArea(!isShowTextArea);
    let pointer = fabricCanvasRef.current.getPointer(o.e);
    setTextAreaPosX(pointer.x);
    setTextAreaPosY(pointer.y);
    // TextAreaにフォーカスする処理
    const timerId = setInterval(() => {
      textAreaRef.current?.focus();
      clearInterval(timerId);
    }, 10);
  };

  // テキストの書き込み
  const writeText = () => {
    if (textAreaRef !== null && textAreaRef.current) {
      const element = new fabric.Text(textAreaRef.current?.value, {
        left: textAreaPosX,
        top: textAreaPosY,
        fill: selectColor,
        fontSize: brushwidth + 12,
        fontFamily: "Ariel",
      });
      fabricCanvasRef.current.add(element);
      fabricCanvasRef.current.renderAll();
      textAreaRef.current.value = "";
    }
    setIsShowTextArea(false);
  };

  // 色の選択
  useEffect(() => {
    if (
      fabricCanvasRef.current !== undefined &&
      fabricCanvasRef.current.freeDrawingBrush
    ) {
      fabricCanvasRef.current.freeDrawingBrush.color = selectColor;
    }
  }, [selectColor, fabricCanvasRef]);

  // このキャンバスを破棄
  const handleDelete = () => {
    setIsDelete(true);
    deleteFlag = true;
  };

  return (
    <div
      className="defects-input-container"
      style={{ display: isDelete === false ? "grid" : "none" }}
    >
      <div className="defects-input-canvas-container">
        <div className="color-input-item">
          <button
            className="color-palette-item color-palette color-palette-black"
            type="button"
            onClick={() => setSelectColor("#000000")}
            style={{
              border:
                selectColor === "#000000"
                  ? "4px solid rgba(255,255,255,0.5)"
                  : "",
            }}
          ></button>
          <button
            className="color-palette-item color-palette color-palette-red"
            type="button"
            onClick={() => setSelectColor("#AF3105")}
            style={{
              border:
                selectColor === "#AF3105"
                  ? "4px solid rgba(255,255,255,0.5)"
                  : "",
            }}
          ></button>
          <button
            className="color-palette-item color-palette color-palette-yellow"
            type="button"
            onClick={() => setSelectColor("#FDF36A")}
            style={{
              border:
                selectColor === "#FDF36A"
                  ? "4px solid rgba(255,255,255,0.5)"
                  : "",
            }}
          ></button>
          <Slider
            defaultValue={initialBrushWidth}
            aria-label="Default"
            valueLabelDisplay="auto"
            orientation="vertical"
            min={brushMinWidth}
            max={brushMaxWidth}
            onChange={handleChangeWidth}
            style={{ height: 180, color: "#4AB1CB" }}
          />

          <img
            src="images/undo.svg"
            onClick={() => undo()}
            alt="Icon layout "
            className="edit-button icon"
          />
          <img
            src="images/redo.svg"
            onClick={() => redo()}
            alt="Icon layout "
            className="edit-button icon"
          />
          <img
            src="images/delete_forever.svg"
            onClick={handleDelete}
            data-index={canvasId}
            alt="Icon layout "
            className="edit-button icon"
          />
        </div>

        <div
          className="canvas-input-item fabric-base"
          style={{
            position: "relative",
            width: canvasWidth + "px",
            height: canvasHeight + "px",
            maxWidth: "80vw",
            maxHeight: "80vh",
            overflowX: isScroll ? "auto" : "hidden",
            overflowY: isScroll ? "auto" : "hidden",
            scrollbarWidth: "thin",
          }}
        >
          <div
            style={{
              display: inputMode !== "capture" ? "block" : "none",
              width: canvasWidth + "px",
              height: canvasHeight + "px",
            }}
          >
            <canvas
              id={canvasId}
              ref={canvasRef}
              width={canvasWidth}
              height={canvasHeight}
              style={{
                width: canvasWidth,
                height: canvasHeight,
              }}
            ></canvas>
          </div>
          {inputMode === "capture" && (
            <div style={{ zIndex: 9999 }}>
              <Webcam
                audio={false}
                width={canvasWidth}
                height={canvasHeight}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
              />
            </div>
          )}

          {isShowTextArea && (
            <textarea
              className="canvas-input-text"
              ref={textAreaRef}
              style={{
                width: canvasWidth - textAreaPosX - 5,
                height: canvasHeight - textAreaPosY - 5,
                display: isShowTextArea ? "block" : "none",
                backgroundColor: "transparent",
                color: selectColor,
                zIndex: 1000,
                position: "absolute",
                left: textAreaPosX,
                top: textAreaPosY,
                fontSize: brushwidth + 12,
                resize: "none",
                padding: "0px",
                fontFamily: "Ariel",
              }}
              onBlur={() => writeText()}
            ></textarea>
          )}
        </div>
      </div>

      <div className="button-input-item">
        {inputMode === "capture" && (
          <>
            <img
              src="images/camera.svg"
              alt="Icon layout "
              className="icon"
              onClick={() => capture()}
            />
            <button
              className="input-button"
              type="button"
              onClick={() => capture()}
              style={{
                backgroundColor:
                  inputMode === "capture" ? "#4AB1CC" : "#F0F0F0",
                color: inputMode === "capture" ? "#fff" : "#000",
              }}
            >
              キャプチャ
            </button>
          </>
        )}
        {inputMode !== "capture" && (
          <>
            <img
              src="images/camera.svg"
              alt="Icon layout "
              className="icon"
              onClick={() => setCaptureMode()}
            />
            <button
              className="input-button"
              type="button"
              onClick={() => setCaptureMode()}
            >
              写真を撮る
            </button>
          </>
        )}
        <div></div>

        <img
          src="images/picture.svg"
          alt="Icon layout "
          className="icon"
          onClick={() => inputFileRef.current?.click()}
        />
        <button
          className="input-button"
          type="button"
          onClick={() => inputFileRef.current?.click()}
        >
          写真を選択
        </button>
        <input
          type="file"
          aria-label="写真を選択"
          ref={inputFileRef}
          style={{ display: "none" }}
          onChange={handleSelectFile}
        />
        <div></div>

        <img
          src="images/draw_rect.svg"
          alt="Icon layout "
          className="icon"
          onClick={() => setRectMode()}
        />
        <button
          className="input-button"
          type="button"
          onClick={() => setRectMode()}
          style={{
            backgroundColor: inputMode === "rect" ? "#4AB1CC" : "#F0F0F0",
            color: inputMode === "rect" ? "#fff" : "#000",
          }}
        >
          四角を描く
        </button>
        <div></div>

        <img
          src="images/draw_free.svg"
          alt="Icon layout "
          className="icon"
          onClick={() => setFreehandMode()}
        />
        <button
          className="input-button"
          type="button"
          onClick={() => setFreehandMode()}
          style={{
            backgroundColor: inputMode === "freehand" ? "#4AB1CC" : "#F0F0F0",
            color: inputMode === "freehand" ? "#fff" : "#000",
          }}
        >
          手書き
        </button>
        <div></div>

        <img
          src="images/input_text.svg"
          alt="Icon layout "
          className="icon"
          onClick={() => setTextMode()}
        />
        <button
          className="input-button"
          type="button"
          onClick={() => setTextMode()}
          style={{
            backgroundColor: inputMode === "text" ? "#4AB1CC" : "#F0F0F0",
            color: inputMode === "text" ? "#fff" : "#000",
          }}
        >
          テキスト入力
        </button>
      </div>
    </div>
  );
});
